import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Description as ManifiestoIcon,
  Inbox as EmptyIcon,
} from '@mui/icons-material';

const Manifiesto = () => {
  const [guias, setGuias] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [consignatarios, setConsignatarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data para cada guía (indexado por id)
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(endpoints.guias.manifiesto);
      const data = res.data.data;
      
      setGuias(data.guias || []);
      setShippers(data.shippers || []);
      setConsignatarios(data.consignatarios || []);
      
      // Inicializar formData con valores actuales
      const initialData = {};
      (data.guias || []).forEach(g => {
        initialData[g.id] = {
          finaldest: g.finaldest || '',
          piezasguia: g.piezasguia || 0,
          nature: g.nature || '',
          pesoguia: g.pesoguia || 0,
          vkgs: g.vkgs || 0,
          charge: Math.max(g.pesoguia || 0, g.vkgs || 0),
          shipper: g.shipper || '',
          cnee: g.cnee || '',
          oficial: g.oficial || '',
        };
      });
      setFormData(initialData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setError('Error al cargar guías');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (guiaId, field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [guiaId]: {
          ...prev[guiaId],
          [field]: value,
        }
      };
      
      // Calcular chargeable automáticamente
      if (field === 'pesoguia' || field === 'vkgs') {
        const peso = field === 'pesoguia' ? parseFloat(value) || 0 : parseFloat(updated[guiaId].pesoguia) || 0;
        const vkgs = field === 'vkgs' ? parseFloat(value) || 0 : parseFloat(updated[guiaId].vkgs) || 0;
        updated[guiaId].charge = Math.max(peso, vkgs);
      }
      
      return updated;
    });
  };

  const handleSave = async (guiaId) => {
    setSaving(prev => ({ ...prev, [guiaId]: true }));
    setError('');
    
    try {
      await api.put(endpoints.guias.manifiesto, {
        id: guiaId,
        ...formData[guiaId],
        shipper: formData[guiaId].shipper || null,
        cnee: formData[guiaId].cnee || null,
      });
      setSuccess('Guía actualizada correctamente');
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(prev => ({ ...prev, [guiaId]: false }));
    }
  };

  const handleSaveAll = async () => {
    const guiasToSave = guias.map(g => ({
      id: g.id,
      ...formData[g.id]
    }));
    
    setSaving({ all: true });
    setError('');
    
    try {
      await api.post(endpoints.guias.manifiesto, { guias: guiasToSave });
      setSuccess('Todas las guías actualizadas');
      fetchData();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving({});
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ManifiestoIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Pre-Manifiesto
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  Completar información de guías para vuelos
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="inherit"
                startIcon={<RefreshIcon />}
                onClick={fetchData}
                sx={{ color: 'warning.dark' }}
              >
                Refrescar
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={saving.all ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveAll}
                disabled={saving.all || guias.length === 0}
              >
                Guardar Todo
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Chip
          label={`${guias.length} guía(s) pendiente(s)`}
          color="warning"
          variant="outlined"
        />
      </Box>

      {/* Table */}
      {guias.length === 0 ? (
        <Card>
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">No hay guías pendientes de completar</Typography>
            <Button component={Link} to="/oficina/reservas" sx={{ mt: 2 }}>
              Ir a Reservas
            </Button>
          </Box>
        </Card>
      ) : (
        <Card>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Guía</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Destino</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Piezas</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>Naturaleza</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Peso Bruto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>VKGS</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Chargeable</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Shipper</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Consignee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Uso Oficial</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {guias.map((guia) => (
                  <TableRow key={guia.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                        {guia.guia}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {guia.agente_nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={formData[guia.id]?.finaldest || ''}
                        onChange={(e) => handleInputChange(guia.id, 'finaldest', e.target.value.toUpperCase())}
                        inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                        sx={{ width: 70 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={formData[guia.id]?.piezasguia || ''}
                        onChange={(e) => handleInputChange(guia.id, 'piezasguia', e.target.value)}
                        sx={{ width: 70 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        multiline
                        rows={2}
                        value={formData[guia.id]?.nature || ''}
                        onChange={(e) => handleInputChange(guia.id, 'nature', e.target.value)}
                        fullWidth
                        placeholder="Descripción de mercancía"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={formData[guia.id]?.pesoguia || ''}
                        onChange={(e) => handleInputChange(guia.id, 'pesoguia', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={formData[guia.id]?.vkgs || ''}
                        onChange={(e) => handleInputChange(guia.id, 'vkgs', e.target.value)}
                        sx={{ width: 90 }}
                        inputProps={{ step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={formData[guia.id]?.charge || 0}
                        disabled
                        sx={{ width: 90, '& .MuiInputBase-input': { fontWeight: 'bold' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={formData[guia.id]?.shipper || ''}
                          onChange={(e) => handleInputChange(guia.id, 'shipper', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">-</MenuItem>
                          {shippers.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={formData[guia.id]?.cnee || ''}
                          onChange={(e) => handleInputChange(guia.id, 'cnee', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">-</MenuItem>
                          {consignatarios.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={formData[guia.id]?.oficial || ''}
                        onChange={(e) => handleInputChange(guia.id, 'oficial', e.target.value)}
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Guardar esta guía">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => handleSave(guia.id)}
                          disabled={saving[guia.id]}
                        >
                          {saving[guia.id] ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button component={Link} to="/oficina/reservas" variant="outlined">
          Administrar Reservas
        </Button>
        <Button component={Link} to="/oficina" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        message={success}
      />
    </Box>
  );
};

export default Manifiesto;




